import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const FAQS = [
  {
    q: "Is T.A.C.C. for individuals or teams?",
    a: "Both. Individuals can begin with the Audio, the Course, or the Complete Bundle. Employers and teams can ask about the quarterly workforce offering.",
  },
  {
    q: "Where should I start?",
    a: "The $77 Complete Bundle is the clearest place to start if you want the fullest individual path. The Audio and the Course remain available if you want something more focused.",
  },
  {
    q: "What is included in the workforce offering?",
    a: "The current offering includes bulk paperback discounts, two live pre-shift workshops per quarter, a manager training playbook, and an anonymous impact dashboard. Reach out to discuss fit and practical details.",
  },
];

export const scrollToQuote = () => {
  const el = document.getElementById("quote");
  if (!el) return false;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 96, behavior: "smooth" });
  return true;
};

export const FAQSection = () => {
  const navigate = useNavigate();
  const askTeam = () => { if (!scrollToQuote()) navigate("/b2b#quote"); };

  return (
    <section className="relative bg-[#FAF7F2] py-28 md:py-36 border-b border-[#E7DFD5]" data-testid="faq-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-y-12 md:gap-x-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
          className="col-span-12 md:col-span-5"
        >
          <div className="section-tag mb-6">§ Before you begin</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mb-6" data-testid="faq-heading">
            Questions before you <span className="italic text-[#B45309]">begin</span>?
          </h2>
          <p className="font-display italic text-xl text-[#44403C] mb-4">No guesswork required.</p>
          <p className="text-[#78716C] leading-relaxed max-w-md mb-10">
            T.A.C.C. is built to meet people where they are. If you are deciding between individual support and a team conversation, reach out through the path that feels right.
          </p>
          <button onClick={askTeam} className="btn-outline text-sm" data-testid="faq-team-cta">
            Ask about team support <ArrowUpRight size={16} />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.12, ease: [0.65, 0, 0.35, 1] }}
          className="col-span-12 md:col-span-7"
        >
          <Accordion type="single" collapsible className="border-t border-[#E7DFD5]" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-[#E7DFD5]" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="py-7 hover:no-underline group [&>svg]:text-[#B45309] [&>svg]:h-5 [&>svg]:w-5" data-testid={`faq-trigger-${i}`}>
                  <span className="flex items-baseline gap-5 text-left">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-[#78716C] shrink-0">0{i + 1}</span>
                    <span className="font-display text-2xl md:text-3xl leading-tight text-[#1C1917] group-hover:text-[#B45309] transition-colors duration-300">{f.q}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-8 pl-[calc(1.25rem+2ch)] text-base text-[#78716C] leading-relaxed max-w-2xl" data-testid={`faq-content-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
